import { userActionTypeEnum } from '@thoughtspot/metrics';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { IconColor, IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import React, {
    Dispatch,
    SetStateAction,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '/@contexts/global-app-context';
import {
    collectOneMindBotMetrics,
    hideOneMindBot,
    ONE_MIND_SOURCE,
    ONE_MIND_URL,
} from '../../utils/1mind-utils';
import styles from './1Mind-modal.container.module.scss';

export interface OneMindModalProps {
    showOneMindModal: boolean;
    setShowOneMindModal: Dispatch<SetStateAction<boolean>>;
    hideBot?: boolean;
}

export const OneMindModal: React.FC<OneMindModalProps> = ({
    showOneMindModal,
    setShowOneMindModal,
    hideBot = false,
}) => {
    const { metricsService } = useAppContext();
    const location = useLocation();

    useEffect(() => {
        if (hideBot) {
            hideOneMindBot(location.pathname, hideBot);
        }
    }, [hideBot, location.pathname]);

    const closeOneMindModal = () => {
        setShowOneMindModal(false);
        hideOneMindBot(location.pathname);
        collectOneMindBotMetrics(
            metricsService,
            userActionTypeEnum.ONE_MIND_CLOSED,
            ONE_MIND_SOURCE.BANNER,
        );
    };

    const [sessionId, setSessionId] = useState<string | null>(null);
    const [showLoading, setShowLoading] = useState(true);
    const oneMindLoadedRef = useRef(false);

    // Capture clicks on the OneMind banner container
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== ONE_MIND_URL) {
                return;
            }

            if (
                event.data?.type === '1mind_embed_loaded' &&
                !oneMindLoadedRef.current
            ) {
                collectOneMindBotMetrics(
                    metricsService,
                    userActionTypeEnum.ONE_MIND_LOADED,
                    ONE_MIND_SOURCE.BANNER,
                );
                oneMindLoadedRef.current = true;
                setShowLoading(false);
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    useEffect(() => {
        if (showOneMindModal) {
            const iframe = document.getElementById(
                'onemind-iframe',
            ) as HTMLIFrameElement;
            if (iframe?.src) {
                const url = new URL(iframe.src);
                const sessionFromIframe = url.searchParams.get('session');
                setSessionId(sessionFromIframe);
            }
            collectOneMindBotMetrics(
                metricsService,
                userActionTypeEnum.ONE_MIND_CLICKED,
                ONE_MIND_SOURCE.BANNER,
            );
            setShowLoading(true);
        }
    }, [showOneMindModal]);

    return (
        <Modal
            isOpen={showOneMindModal}
            size={ModalSize.FULLPAGE}
            includesHeader={false}
            includesFooter={false}
            onDismiss={closeOneMindModal}
            className={styles.oneMindModal}
            contentClassName={styles.videoModalContainer}
            doNotDismissOnOutsideClickOrEsc
        >
            <div
                className={styles.iframeContainer}
                data-testid="one-mind-modal-video"
            >
                <Button
                    onClick={closeOneMindModal}
                    icon={IconID.CROSS}
                    iconColor={IconColor.WHITE}
                    className={styles.closeButton}
                    data-testid="modal-confirm-button"
                    size="m"
                    type="secondary"
                />

                {showLoading && (
                    <LoadingIndicator.Global
                        className={styles.loadingIndicator}
                    >
                        <div data-testid="loading" />
                    </LoadingIndicator.Global>
                )}

                <iframe
                    src={`${ONE_MIND_URL}/?session=${sessionId}&ui_version=v3&display=widget`}
                    title="OneMind Modal"
                    id="onemind-iframe"
                />
            </div>
        </Modal>
    );
};
